import axios from 'axios';

// Use environment variable if available, otherwise fallback to localhost
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Conversations API
export const conversationsApi = {
  getConversations: async (userId, offset = 0, limit = 20, userEmail = null, userName = null, avatarUrl = null) => {
    const params = { 
      user_id: userId,
      offset,
      limit
    };
    
    // Add optional user details if provided
    if (userEmail) params.user_email = userEmail;
    if (userName) params.user_name = userName;
    if (avatarUrl) params.avatar_url = avatarUrl;
    
    const response = await api.get('/conversations', { params });
    return response.data;
  },

  createConversation: async (userId, title, userEmail = null, userName = null, avatarUrl = null) => {
    const payload = {
      user_id: userId,
      title
    };
    
    // Add optional user details if provided
    if (userEmail) payload.user_email = userEmail;
    if (userName) payload.user_name = userName;
    if (avatarUrl) payload.user_avatar = avatarUrl;
    
    const response = await api.post('/conversations/new', payload);
    return response.data;
  },

  renameConversation: async (conversationId, userId, title) => {
    const response = await api.put(`/conversations/${conversationId}/rename`, {
      user_id: userId,
      title
    });
    return response.data;
  },

  updateConversationTitle: async (conversationId, userId, firstMessage) => {
    // Generate a title based on the first message
    // Limit to 100 characters maximum (with ellipsis if needed)
    let title = firstMessage;
    if (title.length > 97) {
      title = title.substring(0, 97) + '...';
    } else if (title.length > 50) {
      // Keep existing behavior of adding ellipsis at 50 chars for regular titles
      title = title.substring(0, 47) + '...';
    }
    
    // Use the existing rename endpoint
    return await conversationsApi.renameConversation(conversationId, userId, title);
  },

  deleteConversation: async (conversationId, userId) => {
    const response = await api.delete(`/conversations/${conversationId}`, {
      params: { user_id: userId }
    });
    return response.data;
  },
  
  autonameConversation: async (conversationId, userId) => {
    const response = await api.post(`/conversations/${conversationId}/autoname`, {
      user_id: userId
    });
    return response.data; // Should return { status: 'success', title: 'New Title' }
  },

  getConversationDetails: async (conversationId, userId) => {
    const response = await api.get(`/conversations/${conversationId}`, {
      params: { user_id: userId }
    });
    return response.data;
  },
  
  getConversationMessages: async (conversationId, userId, limit = 50, offset = 0) => {
    const response = await api.get(`/conversations/${conversationId}/messages`, {
      params: { 
        user_id: userId,
        limit,
        offset
      }
    });
    return response.data;
  }
};

// Query Processing API
export const queryApi = {
  processQuery: async (query, conversationId, eventHistory = '', imageUrls = [], canvasList = [], rawText = '', isAutomatic = false, messageIds = null) => {
    const payload = {
      query,
      raw_text: rawText,
      conversation_id: conversationId,
      event_history: eventHistory,
      image_urls: imageUrls,
      is_automatic: isAutomatic
    };
    
    // Add message_ids if provided
    if (messageIds && messageIds.length > 0) {
      payload.message_ids = messageIds;
    }
    
    const response = await api.post('/query', payload);
    return response.data;
  },
  
  fetchMessages: async (conversationId, messageIds) => {
    const response = await api.post('/messages/fetch', {
      conversation_id: conversationId,
      message_ids: messageIds
    });
    return response.data;
  }
};

// Code Generation API
export const codeGenApi = {
  generateCode: async (description) => {
    const response = await api.post('/code_gen', {
      description
    });
    return response.data;
  },

  createCanvas: async (userId, conversationId, title, code, access = 'private', userEmail = null, userName = null, avatarUrl = null) => {
    const payload = {
      user_id: userId,
      conversation_id: conversationId,
      title,
      code,
      access
    };
    
    // Add optional user details if provided
    if (userEmail) payload.user_email = userEmail;
    if (userName) payload.user_name = userName;
    if (avatarUrl) payload.user_avatar = avatarUrl;
    
    const response = await api.post('/canvas_create', payload);
    return response.data;
  },

  editCanvas: async (canvasId, currentCode, editCommand) => {
    const response = await api.post('/canvas_edit', {
      content: currentCode,
      command: editCommand,
      canvas_id: canvasId
    });
    return response.data;
  },
  
  getCanvasCode: async (canvasId) => {
    const response = await api.get('/canvas_code', {
      params: { canvas_id: canvasId }
    });
    return response.data;
  }
};
